import React from 'react';
import {
  TextField,
  NumberField,
  VStack,
  SingleSelectField,
  Box,
  OptionType,
  PhoneField,
  TermsOfServiceField,
} from '@medely/ui-kit';
import { OTHER_REFERRAL_ID } from '../constants';
import { useWatch } from '@medely/ui-kit/react-hook-form';
import { isProduction } from '@mp/utils/productionUtils';
import { config } from '@mp/config';
import { useTermsOfServiceAnalytics } from '@mp';
import { useImpersonating } from '@mp/hooks/useImpersonating';
import { useTermsOfService } from '@mp/hooks/useTermsOfService';

type AccountInfoFormProps = {
  isReferralInfoMissing: boolean;
  referralSourceOptions: OptionType[];
  isAgency: boolean;
  proEmail?: string;
  isMobile?: boolean;
};

export const AccountInfoForm = ({
  isReferralInfoMissing,
  referralSourceOptions,
  isAgency,
  proEmail,
  isMobile = false,
}: AccountInfoFormProps) => {
  const selectedReferralSourceId = useWatch({ name: 'referral_source_id' });

  const { trackViewed, trackCompleted } = useTermsOfServiceAnalytics('Application Account Info');

  const showOtherReferralField = selectedReferralSourceId === OTHER_REFERRAL_ID;
  const isImpersonating = useImpersonating();
  const signerId = String(proEmail);
  const testMode = !isProduction || isImpersonating;

  const tosGroupKey = isAgency
    ? config.ironclad.groupKeys.checkbox.tosAgency
    : config.ironclad.groupKeys.checkbox.tosMarketplace;

  const { hasSignedLatestTerms, isLoading: isLoadingTermsStatus } = useTermsOfService({
    accessId: config.ironclad.accessId,
    groupKey: tosGroupKey,
    signerId,
    enabled: !isMobile && !!proEmail,
  });
  const shouldShowTermsField = !isMobile && !isLoadingTermsStatus && !hasSignedLatestTerms;

  return (
    <VStack pt={2} gap={2}>
      <TextField name="first_name" label="First name" testId="account-info-first-name" />
      <TextField name="last_name" label="Last name" testId="account-info-last-name" />
      <TextField
        name="nickname"
        label="Preferred name"
        testId="account-info-nickname"
        helperText="Other than your legal first name"
      />
      <NumberField
        name="address_zip"
        label="Zip code"
        testId="account-info-zip"
        maxDigits={5}
        type="number"
      />
      <PhoneField name="phone_number" label="Phone Number" testId="account-info-phone" />
      {isReferralInfoMissing && (
        <Box testId="account-info-referral-source">
          <SingleSelectField
            name="referral_source_id"
            label="How did you hear about us?"
            variant={isMobile ? 'bottomSheet' : 'default'}
            options={referralSourceOptions}
          />
        </Box>
      )}
      {showOtherReferralField && (
        <TextField
          name="other_referral_source"
          label="Other"
          testId="account-info-other-referral-source"
        />
      )}
      {shouldShowTermsField && (
        <TermsOfServiceField
          name="terms_of_service"
          accessId={config.ironclad.accessId}
          groupKey={tosGroupKey}
          signerId={signerId}
          testMode={testMode}
          onDisplayed={() => trackViewed()}
          formatOnChange={() => trackCompleted()}
        />
      )}
    </VStack>
  );
};
